import { useEffect, useState } from 'react'
import type { ModelData, ConfigurationState } from '../types'

interface ImageCache {
  [key: string]: HTMLImageElement
}

interface UseImagePreloaderReturn {
  currentImage: string
  imageLoaded: boolean
  imageError: boolean
  isLoading: boolean
}

// Global image cache
const imageCache: ImageCache = {}
const preloadingQueue = new Set<string>()

// Preload image function
const preloadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    if (imageCache[src]) {
      resolve(imageCache[src])
      return
    }

    if (preloadingQueue.has(src)) {
      // If already preloading, wait for it (with timeout to prevent infinite loops)
      let attempts = 0
      const maxAttempts = 200 // 10 sekund (200 * 50ms)
      
      const checkInterval = setInterval(() => {
        attempts++
        if (imageCache[src]) {
          clearInterval(checkInterval)
          resolve(imageCache[src])
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval)
          console.warn('Image preload timeout:', src)
          reject(new Error(`Image preload timeout: ${src}`))
        }
      }, 50)
      return
    }

    preloadingQueue.add(src)
    const img = new Image()
    
    img.onload = () => {
      imageCache[src] = img
      preloadingQueue.delete(src)
      resolve(img)
    }
    
    img.onerror = () => {
      preloadingQueue.delete(src)
      reject(new Error(`Failed to load image: ${src}`))
    }
    
    img.src = src
  })
}

// Get all possible images for a model
const getModelImages = (model: ModelData): string[] => {
  const images: string[] = []
  
  // Base image
  images.push(model.image)
  
  // Lift variant if available (check basePrices for lift variant)
  if (model.basePrices && typeof model.basePrices === 'object' && 'lift' in model.basePrices) {
    const lastDotIndex = model.image.lastIndexOf('.')
    const fileName = model.image.substring(0, lastDotIndex)
    const extension = model.image.substring(lastDotIndex)
    images.push(`${fileName}-lift${extension}`)
  }
  
  return images
}

// Generate current image path based on config
const generateImagePath = (model: ModelData, config: ConfigurationState): string => {
  let imagePath = model.image
  
  // Handle LIFT image variants
  if (model.glassType === 'vis-a-vis') {
    const frontVariant = config['front-variant']
    const backVariant = config['back-variant']
    
    if (frontVariant === 'lift' || backVariant === 'lift') {
      const lastDotIndex = imagePath.lastIndexOf('.')
      const fileName = imagePath.substring(0, lastDotIndex)
      const extension = imagePath.substring(lastDotIndex)
      imagePath = `${fileName}-lift${extension}`
    }
  } else {
    if (config.variant === 'lift') {
      const lastDotIndex = imagePath.lastIndexOf('.')
      const fileName = imagePath.substring(0, lastDotIndex)
      const extension = imagePath.substring(lastDotIndex)
      imagePath = `${fileName}-lift${extension}`
    }
  }
  
  return imagePath
}

export const useImagePreloader = (
  model: ModelData | null, 
  config: ConfigurationState
): UseImagePreloaderReturn => {
  const [currentImage, setCurrentImage] = useState<string>('')
  const [imageLoaded, setImageLoaded] = useState<boolean>(false)
  const [imageError, setImageError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Preload all model images when model changes
  useEffect(() => {
    if (!model) return

    const modelImages = getModelImages(model)
    
    // Preload all images for this model in background
    modelImages.forEach(imageSrc => {
      preloadImage(imageSrc).catch(() => {
        // Silently fail for preloading - main image load will handle errors
        console.warn(`Failed to preload image: ${imageSrc}`)
      })
    })
  }, [model])

  // Update current image when model or config changes
  useEffect(() => {
    if (!model) {
      setCurrentImage('')
      setImageLoaded(false)
      setImageError(false)
      setIsLoading(false)
      return
    }

    const newImagePath = generateImagePath(model, config)
    setCurrentImage(newImagePath)
    setImageLoaded(false)

    // Check if image is already cached first
    if (imageCache[newImagePath]) {
      setImageError(false)
      setIsLoading(false)
      setImageLoaded(true)
      return
    }

    // Start with error state, will be corrected if image loads successfully
    setImageError(true)
    setIsLoading(false)

    // Try to load the specific image
    preloadImage(newImagePath)
      .then(() => {
        setImageLoaded(true)
        setImageError(false)
        setIsLoading(false)
      })
      .catch(() => {
        setImageLoaded(false)
        setImageError(true)
        setIsLoading(false)
      })
  }, [model, config])

  return {
    currentImage,
    imageLoaded,
    imageError,
    isLoading
  }
}

// Preload all images for common models on app startup
export const preloadCommonImages = () => {
  const commonImages = [
    'images/dragon-2.jpg',
    'images/dragon-2-lift.jpg',
    'images/dragon-2-slim.jpg',
    'images/dragon-2-slim-lift.jpg',
    'images/dragon-4.jpg',
    'images/dragon-4-lift.jpg',
    'images/nemo-2.jpg',
    'images/nemo-2-lift.jpg',
    'images/unico-logo.png'
  ]

  commonImages.forEach(imageSrc => {
    preloadImage(imageSrc).catch(() => {
      console.warn(`Failed to preload common image: ${imageSrc}`)
    })
  })
}



// Priority-based image preloading system

// High priority: All category images for main grid (critical path)
export const preloadAllCategoryImages = (modelsData: { [key: string]: { [key: string]: ModelData } }): Promise<void> => {
  const imageUrls = new Set<string>()
  
  // Get first image from each group for category grid
  Object.values(modelsData).forEach(models => {
    const firstModel = Object.values(models)[0]
    if (firstModel?.image) {
      imageUrls.add(firstModel.image)
    }
  })
  
  const promises = Array.from(imageUrls).map(url => 
    preloadImage(url).catch(() => new Image()) // Silent fail
  )
  
  console.log(`[Priority 1] Loading ${imageUrls.size} category images`)
  return Promise.all(promises).then(() => {
    console.log(`[Priority 1] ✅ Category images ready`)
  })
}

// High priority: Single category image (for individual model change)
export const preloadCategoryImages = (categoryModels: { [key: string]: ModelData }): Promise<void> => {
  const imageUrls = new Set<string>()
  
  // Get first image from the category for display
  const firstModel = Object.values(categoryModels)[0]
  if (firstModel?.image) {
    imageUrls.add(firstModel.image)
  }
  
  const promises = Array.from(imageUrls).map(url => 
    preloadImage(url).catch(() => new Image()) // Silent fail
  )
  
  console.log(`[Priority 1] Loading ${imageUrls.size} category images`)
  return Promise.all(promises).then(() => {
    console.log(`[Priority 1] ✅ Category images ready`)
  })
}

// Medium priority: Model images for specific category (on-demand)
export const preloadCategoryModels = (models: { [key: string]: ModelData }): Promise<void> => {
  const imageUrls = Object.values(models)
    .map(model => model.image)
    .filter((url): url is string => Boolean(url) && !imageCache[url])
  
  if (imageUrls.length === 0) return Promise.resolve()
  
  const promises = imageUrls.map(url => 
    preloadImage(url).catch(() => new Image())
  )
  
  console.log(`[Priority 2] Loading ${imageUrls.length} model images`)
  return Promise.all(promises).then(() => {
    console.log(`[Priority 2] ✅ Model images ready`)
  })
}

// Low priority: Visualization images (background, non-blocking)
let backgroundPreloadInProgress = false

export const startBackgroundPreload = (modelsData: { [key: string]: { [key: string]: ModelData } }): void => {
  if (backgroundPreloadInProgress) return
  backgroundPreloadInProgress = true
  
  // Use requestIdleCallback for truly non-blocking background loading
  const processImages = () => {
    const allImages = new Set<string>()
    
    Object.values(modelsData).forEach(models => {
      Object.values(models).forEach(model => {
        getModelImages(model).forEach(img => allImages.add(img))
      })
    })
    
    const uncachedImages = Array.from(allImages).filter(url => !imageCache[url])
    console.log(`[Priority 3] Background loading ${uncachedImages.length} visualization images`)
    
    // Load in small batches to avoid blocking
    const batchSize = 3
    let currentBatch = 0
    
    const loadBatch = () => {
      const batch = uncachedImages.slice(currentBatch * batchSize, (currentBatch + 1) * batchSize)
      if (batch.length === 0) {
        backgroundPreloadInProgress = false
        console.log(`[Priority 3] ✅ Background preload complete`)
        return
      }
      
      Promise.all(batch.map(url => preloadImage(url).catch(() => null)))
        .finally(() => {
          currentBatch++
          // Use setTimeout to yield control back to browser
          setTimeout(loadBatch, 50)
        })
    }
    
    loadBatch()
  }
  
  // Use requestIdleCallback if available, otherwise setTimeout
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(processImages)
  } else {
    setTimeout(processImages, 100)
  }
}

// Optimized hooks with intelligent caching

export const useCategoryGridPreloader = (modelsData: { [key: string]: { [key: string]: ModelData } } | null) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  
  useEffect(() => {
    if (!modelsData) return
    
    setIsLoading(true)
    setIsLoaded(false)
    
    // Priority 1: Load category images (critical path)
    preloadAllCategoryImages(modelsData)
      .then(() => {
        setIsLoading(false)
        setIsLoaded(true)
        
        // Priority 3: Start background preload (non-blocking)
        startBackgroundPreload(modelsData)
      })
      .catch(() => {
        setIsLoading(false)
        setIsLoaded(true)
      })
  }, [modelsData])
  
  return { isLoading, isLoaded }
}

export const useModelGridPreloader = (models: { [key: string]: ModelData } | null) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)
  
  useEffect(() => {
    if (!models) {
      setIsLoading(false)
      setIsLoaded(false)
      return
    }
    
    setIsLoading(true)
    setIsLoaded(false)
    
    // Priority 2: Load model images (on-demand)
    preloadCategoryModels(models)
      .then(() => {
        setIsLoading(false)
        setIsLoaded(true)
      })
      .catch(() => {
        setIsLoading(false)
        setIsLoaded(true)
      })
  }, [models])
  
  return { isLoading, isLoaded }
}
